import React from 'react';
import PropTypes from 'prop-types';
import Formsy from 'formsy-react';
import moment from 'moment';
import { Box } from '@material-ui/core';
import { FormsySelect } from 'formsy-material-ui/lib';
import MenuItem from 'material-ui/MenuItem';
import FormClass from '../shared/FormClass';
import { isBetweenFromAndToDates } from '../../../../utils/dates';
import { FormsyDate, FormsyTime } from '../../../../components/common/Formsy';
import ReviewDatePicker from '../../../../containers/Treatments/ReviewDatePicker/ReviewDatePickerContainer';
import SpecificTimeDetails from './SpecificTimeDetails';
import { recurrenceTypes, actionTypes } from '../../../../settings/constants';
import globals from '../../../../globals/communityGlobals';
import { textFieldStyle, datePickerStyle, dropDownStyle, common } from '../../../../theme/AllocateBaseTheme';

const locale = globals.getArea('treatments');
const errors = globals.getArea('error');

const recurrenceTextMap = {
  [recurrenceTypes.DAILY]: locale.getMsg('days'),
  [recurrenceTypes.WEEKLY]: locale.getMsg('weeks'),
  [recurrenceTypes.MONTHLY]: locale.getMsg('months'),
};

class SpecificTime extends FormClass {
  isSpecific = true;
  combineRefs = el => {
    this.props.innerRef(el);
    this.form = el;
  };

  getDateFromMinValue = referralRecievedDate => {
    const today = globals.now;
    const actionType = this.props.actionType;

    if (actionType === actionTypes.EDIT) {
      if (today.isAfter(referralRecievedDate, 'day')) return today;
      return referralRecievedDate;
    }
    return referralRecievedDate;
  };

  render() {
    const {
      defaultValues,
      values,
      innerRef,
      readOnly,
      actionType,
      referralDateReceived,
      referralServiceEndDate,
      isImportedFromSystmOne,
      ...rest
    } = this.props;
    const { recurrenceType, recurEvery } = this.state;
    const { isAtLeastTodayOrUnmodified } = this;
    return (
      <Formsy.Form {...rest} ref={this.combineRefs} onChange={this.handleChange}>
        <Box display="flex">
          <Box p={1} flexGrow={1} style={{ width: '33.3%' }}>
            <FormsyDate
              name="dateFrom"
              required
              floatingLabelText={`${locale.getMsg('dateFrom')}*`}
              disabled={readOnly || isImportedFromSystmOne}
              value={values.dateFrom ? new Date(values.dateFrom) : undefined}
              requiredError={errors.getMsg('requiredFieldValidationMessage')}
              validations={{
                isAtLeastTodayOrUnmodified: isAtLeastTodayOrUnmodified('dateFrom'),
              }}
              validationErrors={{
                isAtLeastTodayOrUnmodified: locale.getMsg('mustBeAtLeastToday', [locale.getMsg('dateFrom')]),
              }}
              className="date-picker"
              floatingLabelStyle={common.floatingLabelStyle}
              floatingLabelShrinkStyle={common.floatingLabelShrinkStyle}
              floatingLabelFocusStyle={common.floatingLabelFocusStyle}
              underlineStyle={common.underlineStyle}
              textFieldStyle={datePickerStyle.textFieldStyle}
              inputStyle={textFieldStyle.inputStyle}
              style={datePickerStyle.fillContainerStyle}
              errorStyle={common.errorStyle}
              minDate={new Date(this.getDateFromMinValue(moment(referralDateReceived)))}
            />
          </Box>
          <Box p={1} flexGrow={1} style={{ width: '33.3%' }}>
            <FormsyDate
              name="dateTo"
              floatingLabelText={locale.getMsg('dateTo')}
              disabled={readOnly || isImportedFromSystmOne}
              value={values.dateTo ? new Date(values.dateTo) : undefined}
              validations={{
                isAfterDateFrom: null,
                isBeforeEndDate: referralServiceEndDate,
              }}
              validationErrors={{
                isAfterDateFrom: locale.getMsg('cannotBeEarlier', [locale.getMsg('dateFrom')]),
                isBeforeEndDate: 'The treatment end date is outside of the Referral service validity period',
              }}
              className="col-c3 date-picker"
              floatingLabelStyle={common.floatingLabelStyle}
              floatingLabelShrinkStyle={common.floatingLabelShrinkStyle}
              floatingLabelFocusStyle={common.floatingLabelFocusStyle}
              underlineStyle={common.underlineStyle}
              textFieldStyle={datePickerStyle.textFieldStyle}
              inputStyle={textFieldStyle.inputStyle}
              style={datePickerStyle.fillContainerStyle}
              errorStyle={common.errorStyle}
            />
          </Box>
          <Box p={1} flexGrow={1} style={{ width: '33.3%' }} />
        </Box>
        <Box display="flex">
          <Box p={1} flexGrow={1} style={{ width: '33.3%' }}>
            <FormsyTime
              name="specificTimeFrom"
              floatingLabelText={`${locale.getMsg('treatmentStart')}*`}
              className="time-picker-wrapper"
              minutesStep={5}
              required
              requiredError={errors.getMsg('requiredFieldValidationMessage')}
              disabled={readOnly || isImportedFromSystmOne}
              value={values.specificTimeFrom ? new Date(values.specificTimeFrom) : undefined}
              floatingLabelStyle={common.floatingLabelStyle}
              floatingLabelShrinkStyle={common.floatingLabelShrinkStyle}
              floatingLabelFocusStyle={common.floatingLabelFocusStyle}
              underlineStyle={common.underlineStyle}
              textFieldStyle={datePickerStyle.textFieldStyle}
              inputStyle={textFieldStyle.inputStyle}
              errorStyle={common.errorStyle}
            />
          </Box>
          <Box p={1} flexGrow={1} style={{ width: '33.3%' }}>
            <ReviewDatePicker
              pattern={this.createPatternForReviewPicker()}
              selectionMatrix={this.getSelectionMatrixForReviewPicker()}
              floatingLabelText={`${locale.getMsg('reviewDate')}`}
              name="reviewDate"
              value={values.reviewDate ? new Date(values.reviewDate) : undefined}
              disabled={readOnly || isImportedFromSystmOne}
              validations={{
                isBetweenFromAndToDates,
                isAtLeastTodayOrUnmodified: isAtLeastTodayOrUnmodified('reviewDate'),
              }}
              validationErrors={{
                isBetweenFromAndToDates: locale.getMsg('isBetweenFromAndToDatesValidation'),
                isAtLeastTodayOrUnmodified: locale.getMsg('mustBeAtLeastToday', [locale.getMsg('reviewDate')]),
              }}
              floatingLabelStyle={common.floatingLabelStyle}
              floatingLabelShrinkStyle={common.floatingLabelShrinkStyle}
              floatingLabelFocusStyle={common.floatingLabelFocusStyle}
              underlineStyle={common.underlineStyle}
              textFieldStyle={datePickerStyle.textFieldStyle}
              inputStyle={textFieldStyle.inputStyle}
              className="date-picker col-c3"
              errorStyle={common.errorStyle}
            />
          </Box>
          <Box p={1} flexGrow={1} style={{ width: '33.3%' }} />
        </Box>
        <Box display="flex" p={1}>
          <Box p={1} flexGrow={1} style={{ width: '33.3%' }}>
            <FormsySelect
              name="recurrenceType"
              floatingLabelText={locale.getMsg('recurrenceType')}
              value={recurrenceType.selected}
              disabled={recurrenceType.disabled || readOnly || isImportedFromSystmOne}
              className="col-c3"
              floatingLabelStyle={common.floatingLabelStyle}
              floatingLabelShrinkStyle={common.floatingLabelShrinkStyle}
              floatingLabelFocusStyle={common.floatingLabelFocusStyle}
              underlineStyle={common.underlineStyle}
              style={dropDownStyle.style}
              iconStyle={dropDownStyle.iconStyle}
              labelStyle={dropDownStyle.labelStyle}
              menuStyle={dropDownStyle.menuStyle}
            >
              {recurrenceType.options.map(({ value, name }) => (
                <MenuItem key={`recurrenceOption${value}`} value={value} primaryText={name} />
              ))}
            </FormsySelect>
          </Box>
          <Box p={1} flexGrow={1} style={{ width: '33.3%' }}>
            {!recurEvery.disabled && (
              <FormsySelect
                name="recurEvery"
                floatingLabelText={locale.getMsg('recurEvery')}
                value={recurEvery.selected}
                disabled={recurEvery.disabled || readOnly || isImportedFromSystmOne}
                className="col-c3"
                floatingLabelStyle={common.floatingLabelStyle}
                floatingLabelShrinkStyle={common.floatingLabelShrinkStyle}
                floatingLabelFocusStyle={common.floatingLabelFocusStyle}
                underlineStyle={common.underlineStyle}
                style={dropDownStyle.style}
                iconStyle={dropDownStyle.iconStyle}
                labelStyle={dropDownStyle.labelStyle}
                menuStyle={dropDownStyle.menuStyle}
              >
                {recurEvery.options.map(({ value }) => (
                  <MenuItem key={`recurEveryOption${value}`} value={value} primaryText={value} />
                ))}
              </FormsySelect>
            )}
          </Box>
          <Box p={1} flexGrow={1} style={{ width: '33.3%' }}>
            <div className="extra-info">
              {!recurEvery.disabled && recurEvery.options.length ? recurrenceTextMap[recurrenceType.selected] : null}
            </div>
          </Box>
        </Box>

        <SpecificTimeDetails
          name="selectedDays"
          recurrenceType={recurrenceType.selected}
          required={recurrenceType.selected === recurrenceTypes.WEEKLY}
          monthlyRecurrenceType={values.monthlyRecurrenceType}
          value={values.selectedDays}
          selectedWeeks={values.selectedWeeks}
          selectedWeekDays={values.selectedWeekDays}
          disabled={readOnly || isImportedFromSystmOne}
        />
      </Formsy.Form>
    );
  }
}

SpecificTime.defaultProps = {
  innerRef: () => {},
  defaultValues: {
    recurEvery: 1,
    recurrenceType: 'Daily',
    dateTo: undefined,
    dateFrom: undefined,
    specificTimeFrom: undefined,
    selectedDays: undefined,
  },
  readOnly: false,
};

SpecificTime.propTypes = {
  defaultValues: PropTypes.shape({
    recurEvery: PropTypes.number,
    recurrenceType: PropTypes.string,
    selectedDays: PropTypes.objectOf(PropTypes.objectOf(PropTypes.bool)),
    dateTo: PropTypes.date,
    dateFrom: PropTypes.date,
    specificTimeFrom: PropTypes.date,
  }),
  values: PropTypes.shape({
    recurEvery: PropTypes.number,
    recurrenceType: PropTypes.string,
    selectedDays: PropTypes.objectOf(PropTypes.objectOf(PropTypes.bool)),
    dateTo: PropTypes.date,
    dateFrom: PropTypes.date,
    specificTimeFrom: PropTypes.date,
  }),
  innerRef: PropTypes.func,
  readOnly: PropTypes.bool,
  actionType: PropTypes.string.isRequired,
};

export default SpecificTime;
